<template>
  <div class="auth-form">
    <v-alert
      transition="fade-transition"
      class="pa-2"
      type="error"
      dismissible
      v-model="showError"
    >
      {{ errorMessage }}
    </v-alert>

    <template v-if="showDomainForm">
      <div class="domain-field mb-3">
        <v-text-field
          label="Account Domain"
          type="text"
          :rules="[rules.required]"
          v-model="accountDomain"
          @input="domainKeyHandler($event)"
          class="background-dark"
        />
        <span>.{{ domain }}</span>
      </div>

      <v-btn class="mt-3" color="appMain" @click="openSubdomain">
        Continue
      </v-btn>
    </template>

    <template v-else>
      <p class="mb-2">
        You are logging into
        <strong v-if="loginInfo">{{ loginInfo.domain }}</strong>
      </p>
      <p>
        <a :href="baseLoginPath">Click here</a>
        to log into a different organization's domain.
      </p>

      <div v-if="showOkta">
        <v-btn color="appMain" :href="loginInfo.okta_login_url">
          Login with OKTA
        </v-btn>
      </div>

      <v-form v-else ref="form" v-model="isValid" @submit.prevent>
        <v-text-field
          label="Email"
          type="text"
          :rules="[rules.required, rules.isEmail]"
          v-model="form.email"
          class="mb-3 background-dark"
        />

        <v-text-field
          label="Password"
          :type="showPassword ? 'text' : 'password'"
          :rules="[rules.required]"
          v-model="form.password"
          class="mb-1 background-dark"
          :append-icon="showPassword ? 'visibility_off' : 'visibility'"
          @click:append="toggleShowPassword"
        />

        <div>
          <router-link :to="{ name: 'app-password-reset' }">
            Forget Password?
          </router-link>
        </div>

        <div class="d-flex">
          <v-btn class="mt-6" color="appMain" @click="sendForm" type="submit">
            Login
          </v-btn>
        </div>
      </v-form>
    </template>

    <div class="spinner-overlay" v-if="loading">
      <spinner />
    </div>
  </div>
</template>

<script>
import validation from '@/lib/validationRules';
import { mapActions, mapState, mapMutations } from 'vuex';

export default {
  name: 'LoginForm',
  data() {
    return {
      form: {
        email: '',
        password: '',
        domain: '',
      },
      isValid: false,
      showPassword: false,
      errorMessage: '',
      accountDomain: '',
      rules: {
        required: (v) => validation.checkRequired(v) || 'Field is required',
        isEmail: (v) => validation.checkEmail(v) || 'Email is invalid',
      },
    };
  },
  computed: {
    ...mapState('auth', ['loading', 'loginInfo']),
    baseLoginPath() {
      let domain = this.loginInfo?.base_domain || '';
      if (domain && !domain.includes('app.')) {
        domain = domain.replace('://', `://app.`);
      }
      return `${domain}/ui/login`;
    },
    showError: {
      get() {
        return !!this.errorMessage;
      },
      set() {
        this.errorMessage = '';
      },
    },
    showOkta() {
      return !!this.loginInfo?.okta_login_url;
    },
    showDomainForm() {
      const { subdomain, hide_domain } = this.loginInfo || {};
      return !subdomain && !hide_domain;
    },
    domain() {
      return this.loginInfo?.domain || 'conveyour.com';
    },
  },
  methods: {
    ...mapActions('users', ['loadCurrentUser']),
    ...mapActions('auth', ['login', 'getLoginInfo']),
    ...mapMutations('users', ['setCurrentUserLoaded']),
    sendForm() {
      if (!this.isValid) return;

      this.form.domain = this.loginInfo?.subdomain;
      this.login(this.form)
        .then(() => {
          this.onSuccess();
        })
        .catch((err) => {
          this.onError(err);
        });
    },

    onSuccess() {
      this.setCurrentUserLoaded(false);
      this.$router.push('/');
    },

    onError(err) {
      this.errorMessage = err?.data?.message;
      this.$refs.form.reset();
    },

    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },

    domainKeyHandler(val) {
      setTimeout(() => {
        this.accountDomain = val
          .replace(/[^a-zA-Z0-9-]+/g, '')
          .replace(/\s+/g, '')
          .trim()
          .toLowerCase();
      });
    },

    openSubdomain() {
      if (!this.accountDomain) return;

      let url = window.location.href.replace('app.', `${this.accountDomain}.`);

      let baseDomain = this.loginInfo?.base_domain || '';
      if (baseDomain) {
        url = baseDomain.replace('://', `://${this.accountDomain}.`);
        url += '/ui/login';
      }
      window.location.href = url;
    },
  },
  mounted() {
    const { code } = this.$route?.query || {};
    this.getLoginInfo(code);
  },
};
</script>

<style lang="less">
.domain-field {
  display: flex;
  align-items: center;

  & > span {
    font-weight: bold;
    font-size: 16px;
    padding-left: 4px;
  }
}
</style>
